/**
 * Microphone input level.
 *
 * A screen recording shows you what it is capturing; a voice note shows you
 * nothing. Without a meter the only way to discover that the wrong input was
 * selected, or that the mic was muted in hardware, is to record five minutes
 * of silence and find out afterwards. So the meter is not decoration — it is
 * the audio recorder's viewfinder.
 *
 * Measured as RMS over the time domain, mapped through decibels rather than
 * linear amplitude: speech at a comfortable level sits around 0.05 linear,
 * which on a linear meter is indistinguishable from silence.
 */

/** Everything quieter than this reads as silence. */
const FLOOR_DB = -60;

/** Sustained level below this while recording means "we are hearing nothing". */
export const SILENCE_THRESHOLD = 0.06;

export class InputLevel {
  private readonly context: AudioContext;
  private readonly source: MediaStreamAudioSourceNode;
  private readonly analyser: AnalyserNode;
  private readonly gate: GainNode;
  private readonly samples: Float32Array;
  private closed = false;

  constructor(stream: MediaStream) {
    this.context = new AudioContext();
    this.source = this.context.createMediaStreamSource(stream);
    this.analyser = this.context.createAnalyser();
    // 1024 samples is ~21ms at 48kHz: long enough to be stable, short enough
    // that the meter tracks a syllable rather than a sentence.
    this.analyser.fftSize = 1024;
    this.samples = new Float32Array(this.analyser.fftSize);

    // An AnalyserNode only sees audio while the graph is being pulled, and a
    // graph with no path to the destination is not guaranteed to be. The gate
    // is a silent path: gain of exactly zero, so nothing is ever played back
    // into the room the microphone is listening to.
    this.gate = this.context.createGain();
    this.gate.gain.value = 0;
    this.source.connect(this.analyser);
    this.analyser.connect(this.gate);
    this.gate.connect(this.context.destination);

    this.ensureRunning();
  }

  /**
   * An AudioContext created outside a user gesture can start suspended, and a
   * suspended graph reports silence — which would have the meter accusing a
   * working microphone of being broken. This is called on construction and
   * again on the next interaction, which is enough for every browser that
   * defers the first attempt.
   */
  ensureRunning(): void {
    if (this.closed || this.context.state === 'running') return;
    void this.context.resume().catch(() => {
      // Still blocked. `running` stays false and the meter says nothing rather
      // than reporting a level it cannot measure.
    });
  }

  /** False while the graph is suspended, so callers can hold their tongue. */
  get running(): boolean {
    return !this.closed && this.context.state === 'running';
  }

  /** Current level, 0 (silence) to 1 (clipping). */
  read(): number {
    if (this.closed) return 0;
    this.analyser.getFloatTimeDomainData(this.samples);

    let sum = 0;
    for (const sample of this.samples) sum += sample * sample;
    const rms = Math.sqrt(sum / this.samples.length);
    if (rms <= 0) return 0;

    const db = 20 * Math.log10(rms);
    if (db <= FLOOR_DB) return 0;
    return Math.min(1, db / -FLOOR_DB + 1);
  }

  close(): void {
    if (this.closed) return;
    this.closed = true;
    try {
      this.source.disconnect();
      this.analyser.disconnect();
      this.gate.disconnect();
    } catch {
      // Already torn down with the stream.
    }
    void this.context.close().catch(() => {});
  }
}
